import type { DocumentProcessorServiceClient } from '@google-cloud/documentai';
import type { Storage } from '@google-cloud/storage';
import type { GenerationConfig } from '@google-cloud/vertexai';
import type { ObjectId } from 'mongodb';
import type { WithId } from 'mongodb';

import { ActivationDocumentTypes, DocumentTypes, InstallDocumentTypes, ViewUrlType } from './DocumentTypes';
import type { AllDocumentTypes } from './DocumentTypes';
import type { Meta } from './Entities';
import type { ExecutionContext } from './ExecutionContext';
import type { WellDefinedLogger } from './Logs';

/** All prompts need to be defined in MongoDB as PromptTemplateDocument and the matching schema must be in llm/schema directory*/

export interface DocumentPage {
  pageNumber: number;
  width: number;
  height: number;
}

export enum BoundingBoxCoordinateType {
  pixels = 'pixels',
  normalized = 'normalized',
}

export interface BoundingBox {
  fieldName: string;
  pageNumber: number;
  boundingBox: [number, number, number, number];
  coordinateType: BoundingBoxCoordinateType;
}

export interface DocumentAIToken {
  text: string;
  bbox: number[];
  page: number;
  confidence: number;
  startIndex: number;
  length: number;
}

export interface DocumentAIIndexEntry {
  documentId: string;
  text: string;
  pages: Array<{
    pageNumber: number;
    width: number;
    height: number;
  }>;
  tokens: Array<DocumentAIToken>;
  error?: string;
  /** Document quality signals detected by Document AI */
  documentQuality?: DocumentQuality;
}

export type DocumentAIIndex = Array<DocumentAIIndexEntry>;

export interface DocumentLayout {
  pages: DocumentPage[];
  boundingBoxes: BoundingBox[];
}

/**
 * Severity level for quality defects detected in documents
 */
export type QualityDefectSeverity = 'low' | 'medium' | 'high';

/**
 * Individual quality defect detected in a document
 */
export interface QualityDefect {
  type: string;
  confidence: number;
  severity?: QualityDefectSeverity;
  page?: number;
}

/**
 * Document quality signals from Document AI
 * Captures image quality issues that may affect extraction accuracy
 */
export interface DocumentQuality {
  overallQualityScore: number;
  qualityDefects?: QualityDefect[];
  hasQualityIssues: boolean;
  qualityIssueScore?: number;
}

/**
 * Threshold for flagging documents with quality issues
 * Documents with qualityIssueScore above this value will be flagged
 */
export const documentQualityThreshold = 0.7;

export interface PromptTemplate {
  name: string;
  version: number;
  isCurrent: boolean;
  description?: string;
  documentType?: AllDocumentTypes;
  promptText: string;
  model: string; // the model to use for this prompt, e.g. 'gemini-2.0-flash'
  modelLocation?: string; // the Vertex AI location for this model, e.g. 'us-central1', 'global'
  /**
   * The file in llm/schema directory that defines the response schema.
   * If not provided, the response will be provided as an object with the text as a single property.
   */
  responseSchemaFileName?: string;
  /**
   * Temperature for generation (0.0-2.0). Lower values are more deterministic.
   * If not provided, defaults to 0.2.
   */
  temperature?: number;
  /**
   * Maximum file size in bytes for image files sent via GCS URI.
   * Defaults to Gemini Flash 2.5 limit: 31_457_280 (30 MB).
   */
  maxImageGcsFileSizeBytes?: number;
  /**
   * Maximum file size in bytes for image data sent inline (base64).
   * Defaults to Gemini Flash 2.5 limit: 7_340_032 (7 MB).
   */
  maxImageInlineFileSizeBytes?: number;
  /**
   * Maximum file size in bytes for PDF files sent via GCS URI.
   * Defaults to Gemini Flash 2.5 limit: 52_428_800 (50 MB).
   */
  maxPdfGcsFileSizeBytes?: number;
  /**
   * Maximum number of pages per PDF file.
   * Defaults to Gemini Flash 2.5 limit: 1000.
   */
  maxPdfPages?: number;
}
export type PromptTemplateDocument = WithId<PromptTemplate> & { meta: Meta };

export type ModelOutputStatus = 'pending' | 'inProgress' | 'completed' | 'failed' | 'unretryable';

export interface ModelOutput {
  promptTemplateId?: ObjectId;
  promptTemplateName?: string;
  promptTemplateVersion?: number;
  model: string;
  requestHash?: string; // sha256 of the canonicalized request; indexed dedup key. The full request and rawVertexAiText are logged to Datadog, not stored.
  response: Record<string, any> & { documentLayout?: DocumentLayout };
  references?: Record<string, any>;
  expectedOutputs?: Record<string, any>;
  score: number; // score between 0 and 1, where 1 is a perfect match
  executionTimeMs?: number; // time taken to execute the prompt in milliseconds
  tokenCount?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  history?: Array<{
    date: Date;
    description: string; // e.g. 'Pushed arrays from shade report to design', 'Approved PTO', 'Reported issue with extraction'
    reason?: string; // machine-readable reason code for programmatic querying (e.g. UtilityBillExtractionCheckFailureReason)
    additionalContext?: Record<string, any>; // not sure what we might put in here yet
    username: string; // human or machine user who made the change (used for display)
    isAgent?: boolean; // for easier filtering for reports
  }>;
  status: ModelOutputStatus;
  claimedAt?: Date; // set atomically when a worker claims the record
  completedAt?: Date; // set when status transitions to completed
  failureReason?: string; // last error message, overwritten on each attempt
  attemptCount: number; // total extraction attempts, including initial + all retries
}
export type ModelOutputDocument = WithId<ModelOutput> & { meta: Meta };

export const supportedDocumentExtractTypes: AllDocumentTypes[] = [
  DocumentTypes.utilityBill,
  ActivationDocumentTypes.ptoConfirmation,
  InstallDocumentTypes.shadeReport,
  InstallDocumentTypes.planSet,
  InstallDocumentTypes.productionModel,
];

/**
 * MIME types supported by Google Vertex AI (Gemini) for multimodal content
 * @see https://cloud.google.com/vertex-ai/generative-ai/docs/multimodal/send-multimodal-prompts
 */
export const VertexAISupportedMimeTypes = [
  // Documents
  'application/pdf',
  'text/plain',
  // Images
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

export type VertexAISupportedMimeType = (typeof VertexAISupportedMimeTypes)[number];

/**
 * MIME types supported by Google Document AI for document processing
 * @see https://cloud.google.com/document-ai/docs/file-types
 */
export const DocumentAISupportedMimeTypes = [
  // Documents
  'application/pdf',
  // Images
  'image/gif',
  'image/tiff',
  'image/jpeg',
  'image/png',
  'image/bmp',
  'image/webp',
  // Rich documents (layout parser only)
  'text/html',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
] as const;

export type DocumentAISupportedMimeType = (typeof DocumentAISupportedMimeTypes)[number];

export interface DocumentUriEntry {
  documentId: string;
  gcsUri: string;
  mimeType: string;
  error?: string;
  /** Whether this MIME type is supported by Document AI */
  validForDocumentAI?: boolean;
  /** Whether this MIME type is supported by Vertex AI */
  validForVertexAI?: boolean;
  /** File size in bytes, populated from GCS metadata during document retrieval */
  fileSizeBytes?: number;
  /** Pre-resized variants available for this file (HTTP signed URLs) */
  viewUrls?: { url: string; type: ViewUrlType }[];
}

export interface DocumentExtractState {
  documentIds: string[];
  documentType: AllDocumentTypes;
  accountId: string;
  gcsUri?: string;
  documentUris?: DocumentUriEntry[];
  documentAiIndex?: DocumentAIIndex;
  promptTemplate?: PromptTemplateDocument;
  generationConfig?: GenerationConfig;
  extractedData?: Record<string, any>[];
  cachedResults?: Record<string, any>[]; // Results retrieved from cache
  errors?: string[];
  processedCount: number;
  allCached?: boolean; // True if all results were from cache
  partialCache?: boolean; // True if some results were from cache
}

export interface DocumentExtractInput {
  documentIds: string[];
  documentType: AllDocumentTypes;
  executionContext: ExecutionContext;
  accountId: string;
}

export interface AgentConfig {
  documentAiProjectId: string;
  documentAiProcessorId: string;
  documentAiProcessorLocation: string;
  vertexAiProject: string;
  vertexAiLocation: string;
  langsmithApiKey: string;
  langsmithProject: string;
  langsmithTracing: boolean;
}

export interface AgentContext {
  executionContext: ExecutionContext;
  storage: Storage;
  documentAiClient: DocumentProcessorServiceClient;
  logger: WellDefinedLogger;
  config: AgentConfig;
}

export const GenAiQcUserName = 'QC AI Agent';
